import { Injectable } from "@angular/core";
import {
    ERateType, TravellerPrimitiveXType
} from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import {
    BookingType,
    KeywordInDifferenceType,
    KeywordType,
    LuggageInDifferenceType,
    PenaltyInDifferenceType,
    PriceInBookingType,
    PriceItemType,
    RateInDifferenceType,
    SectorInDifferenceType,
} from "@afidium/ts-facade";
import { CurrencyUnitTotalType } from "@afidium/ts-facade/model/currencyUnitTotalType";
import { EColumnType, EPenaltyType, ETarificationArrayType, ETarificationLineType } from "../booking-price-card.enum";
import { SectorXRefType } from "@afidium/ts-facade/model/sectorXRefType";
import { RateInBookingType } from "@afidium/ts-facade/model/rateInBookingType";
import { RuleType } from "@afidium/ts-facade/model/ruleType";
import { PriceDetailInBookingType } from "@afidium/ts-facade/model/priceDetailInBookingType";
import { FareComponentInBookingType } from "@afidium/ts-facade/model/fareComponentInBookingType";
import { TotalPerTravellerType } from "@afidium/ts-facade/model/totalPerTravellerType";
import { TravellerType } from "@afidium/ts-facade/model/travellerType";
import { PriceTotalsInBookingType } from "@afidium/ts-facade/model/priceTotalsInBookingType";
import type { XReasonType } from "@afidium/ts-facade/model/xReasonType";
import { Yii } from "../../../../../interfaces/Yii";
import { ServiceInBookingType } from "@afidium/ts-facade/model/serviceInBookingType";
import { LabelService } from "libraries/service/label/label.service";
import { Subject } from "rxjs";
import { SectorInBookingType } from "@afidium/ts-facade/model/sectorInBookingType";
import { SectorXRefWithSeatCodeType } from "@afidium/ts-facade/model/sectorXRefWithSeatCodeType";
import { DifferenceType } from "@afidium/ts-facade/model/differenceType";
import { PenaltyType } from "@afidium/ts-facade/model/penaltyType";
import { SavingType } from "@afidium/ts-facade/model/savingType";
import { PriceRealAmountPipe } from "libraries/pipes/price/priceRealAmount.pipe";
import { XRoleForRuleType } from "@afidium/ts-facade/model/xRoleForRuleType";
import { SafeHtml } from "@angular/platform-browser";
import { LuggageUnitPipe } from "../pipes/luggage-unit.pipe";
import { PriceTotalsContentInBookingType } from "@afidium/ts-facade/model/priceTotalsContentInBookingType";
import {
    ISectorAdultDifferences,
    ISectorDetail,
    ISectorDetailWithDifferences,
    ISectorDifferences,
    ISectorSeatAndDates
} from "libraries/interfaces/fare-optimisation/ISector";


export interface IPricesTable {
    rateTitle: string;
    rateId?: string;
    rateXRole?: string;
    totalValue: CurrencyUnitTotalType | string;
}

export interface ITarificationPricesTable {
    lineType: string;
    totalOrBaseOrTaxValue: { rate: CurrencyUnitTotalType, optimisation: CurrencyUnitTotalType };
}

export interface IRuleTable {
    sectorLine: string;
}

export interface ICancelAndExchange {
    exchangeable: boolean;
    cancellable: boolean;
}

@Injectable()
export class PricesTableService {
    public rateSelected: string;
    public rateSelectedSubject: Subject<string> = new Subject<string>();

    constructor(private readonly labelService: LabelService,
                private readonly priceRealAmount: PriceRealAmountPipe,
                private readonly luggageUnit: LuggageUnitPipe) {
    }

    public setRateSelected(selectedRateId: string): void {
        this.rateSelected = selectedRateId;
        this.rateSelectedSubject.next(selectedRateId);
    }

    public removeRateSelected(): void {
        this.rateSelected = null;
        this.rateSelectedSubject.next(null);
    }

    public getRateTypeTitle(price: PriceInBookingType, costOrRetail: string, services: ServiceInBookingType[]): string {
        switch (price[costOrRetail]?.xRole) {
            case ERateType.Fee:
                return `${Yii.t('wui-sasha-js', 'Frais')}${this.getFeeData(price, costOrRetail)}`;
            case ERateType.Service:
                return `${Yii.t('wui-sasha-js', 'Service')}${this.getServiceData(services, price, costOrRetail)}`;
            default:
                return `${Yii.t('wui-sasha-js', 'Itinéraire')}${price[costOrRetail]?.index ? ' ' + price[costOrRetail]?.index : ''}`;
        }
    }

    public constructData(
        priceArray: PriceInBookingType[],
        travellers: TravellerType[],
        costOrRetail: string,
        bookingTotal: PriceTotalsInBookingType,
        services: ServiceInBookingType[]
    ): IPricesTable[] {

        let priceTableOutput: IPricesTable[] = [];

        if (Boolean(priceArray?.length)) {
            priceTableOutput = priceArray.filter(
                (price: PriceInBookingType): boolean => {
                    return price[costOrRetail]?.type?.toLowerCase() === "storedfare" &&
                        price[costOrRetail]?.xRank?.toLowerCase() !== "dummy";
                })
                .map((price: PriceInBookingType): IPricesTable => {
                    const xRole: string = price[costOrRetail]?.xRole;
                    const total: PriceItemType = price[costOrRetail]?.total?.totalArray?.find(
                        (totalItem: PriceItemType): boolean => {
                            return totalItem.xReason === xRole || (xRole === 'Fee' && totalItem.xReason === "Payment");
                        });
                    const totalValue: CurrencyUnitTotalType = total?.currencyArray.length ? total?.currencyArray[0] : null;

                    const priceValue: IPricesTable = {
                        [EColumnType.rateTitle]: this.getRateTypeTitle(price, costOrRetail, services),
                        rateId: price[costOrRetail].signature || price[costOrRetail].xId,
                        rateXRole: xRole,
                        totalValue,
                    };

                    travellers?.forEach((traveller: TravellerType): void => {
                        const totalTraveller: PriceItemType = total?.totalPerTravellerArray?.find(
                            (totalPerTraveller: TotalPerTravellerType): boolean => {
                                return totalPerTraveller.traveller?.signature === traveller?.signature;
                            })?.total;
                        priceValue[traveller.signature] = totalTraveller?.currencyArray?.length ? totalTraveller?.currencyArray[0] : '-';
                    });
                    return priceValue;
                });
        }
        if (Boolean(bookingTotal)) {
            priceTableOutput = [...priceTableOutput, this.getTotal(bookingTotal[costOrRetail]?.totalArray, travellers)];
        }
        return priceTableOutput;
    }

    public getRateByXRole(xIdOrSignature: string, booking: BookingType, costOrRetail: string): PriceInBookingType {
        if (booking.priceArray) {
            return booking.priceArray?.find(
                (price: PriceInBookingType): boolean => {
                    return price[costOrRetail]?.signature === xIdOrSignature || price[costOrRetail]?.xId === xIdOrSignature;
                }) ?? booking?.priceArray[0];
        }
    }

    public getSectorsByRate(rate: PriceInBookingType, booking: BookingType, costOrRetail: string): SectorInBookingType[] {
        let sectors: SectorInBookingType[];
        if (rate && rate[costOrRetail]?.detailArray?.length) {
            sectors = rate[costOrRetail]?.detailArray[0]?.sectorArray?.map((rateSector: SectorXRefType): SectorInBookingType => {
                return booking?.sectorArray?.find(
                    (sector: SectorInBookingType): boolean => {
                        return sector.signatureArray?.[0] === rateSector.signature || sector.xId === rateSector.xRef;
                    });
            });
        }
        return sectors;
    }

    public getSectorsBySignature(price: PriceInBookingType, booking: BookingType, costOrRetail: string): SectorInBookingType[] {
        return price[costOrRetail]?.detailArray[0]?.sectorArray?.map((rateSector: SectorXRefType): SectorInBookingType => {
            return booking?.sectorArray?.find(
                (sector: SectorInBookingType): boolean => {
                    return sector.signatureArray?.[0] === rateSector.signature || sector.xId === rateSector.xRef;
                });
        });
    }


    public constructPriceDetailData(
        price: PriceInBookingType,
        travellers: TravellerType[],
        costOrRetail: string,
        displayDataByTypoPax?: boolean,
        saving?: SavingType
    ): ITarificationPricesTable[] {
        const values: ITarificationPricesTable[] = [];
        const xRole: string = price[costOrRetail]?.xRole;

        Object.keys(ETarificationLineType).forEach((key: string): void => {
            const totalOrBaseOrTaxObject: string = this.getTotalOrBaseOrTaxType(key);

            const totalOrBaseOrTax: PriceItemType = this.getPriceByXReason(price[costOrRetail]?.total, totalOrBaseOrTaxObject, xRole);
            const totalOrBaseOrTaxSaving: PriceItemType = this.getPriceByXReason(saving?.cost, totalOrBaseOrTaxObject, xRole);

            const totalOrBaseOrTaxValue: CurrencyUnitTotalType = totalOrBaseOrTax?.currencyArray?.length ?
                totalOrBaseOrTax?.currencyArray[0] :
                null;
            const totalOrBaseOrTaxSavingValue: CurrencyUnitTotalType = totalOrBaseOrTaxSaving?.currencyArray?.length ?
                totalOrBaseOrTaxSaving?.currencyArray[0] :
                null;

            if (totalOrBaseOrTaxValue !== null) {
                const priceValue: ITarificationPricesTable = {
                    lineType: key,
                    totalOrBaseOrTaxValue: {
                        rate: totalOrBaseOrTaxValue,
                        optimisation: totalOrBaseOrTaxSavingValue
                    }
                };

                travellers?.forEach((traveller: TravellerType): void => {
                    const travellerSignatureOrXType: string = displayDataByTypoPax ? traveller?.xType : traveller.signature;

                    if (displayDataByTypoPax) {
                        const totalOrBaseOrTaxTraveller: PriceItemType = this.getPriceByTraveller(totalOrBaseOrTax, traveller?.signature);
                        const totalOrBaseOrTaxSavingTraveller: PriceItemType = this.getPriceByTraveller(totalOrBaseOrTaxSaving, traveller?.signature);

                        if (priceValue[travellerSignatureOrXType]) {
                            const currencyCodeRate: string = priceValue[travellerSignatureOrXType].rate?.currencyCode;
                            const getCurrencyRate: CurrencyUnitTotalType = currencyCodeRate ?
                                totalOrBaseOrTaxTraveller?.currencyArray
                                    .find((currencyItem: CurrencyUnitTotalType): boolean => currencyItem.currencyCode === currencyCodeRate) :
                                null;

                            if (priceValue[travellerSignatureOrXType].rate) {
                                if (getCurrencyRate?.unitAmount) {
                                    priceValue[travellerSignatureOrXType].rate.unitAmount =
                                        (parseFloat(priceValue[travellerSignatureOrXType].rate?.unitAmount) + parseFloat(getCurrencyRate?.unitAmount)).toString() ?? '';
                                } else if (getCurrencyRate?.unitValue) {
                                    priceValue[travellerSignatureOrXType].rate.unitAmount =
                                        (parseFloat(priceValue[travellerSignatureOrXType].rate?.unitAmount) + getCurrencyRate?.unitValue).toString() ?? '';
                                }
                            } else if (getCurrencyRate) {
                                priceValue[travellerSignatureOrXType].rate =
                                    totalOrBaseOrTaxTraveller?.currencyArray?.length ?
                                        totalOrBaseOrTaxTraveller?.currencyArray[0] :
                                        null;
                            }

                            const currencyCodeOptimisation: string = priceValue[travellerSignatureOrXType].optimisation?.currencyCode;
                            const rightCurrencyOptimisation: CurrencyUnitTotalType = currencyCodeOptimisation ? totalOrBaseOrTaxSavingTraveller?.currencyArray.find((currencyItem: CurrencyUnitTotalType) => currencyItem.currencyCode === currencyCodeOptimisation) : null;
                            if (priceValue[travellerSignatureOrXType].optimisation) {
                                if (rightCurrencyOptimisation?.amount) {
                                    priceValue[travellerSignatureOrXType].optimisation.amount = (parseFloat(priceValue[travellerSignatureOrXType].optimisation?.amount) + parseFloat(rightCurrencyOptimisation?.amount)).toString() ?? '';
                                } else if (rightCurrencyOptimisation?.unitValue) {
                                    priceValue[travellerSignatureOrXType].optimisation.amount = (parseFloat(priceValue[travellerSignatureOrXType].optimisation?.amount) + rightCurrencyOptimisation?.unitValue).toString() ?? '';
                                }
                            } else if (rightCurrencyOptimisation) {
                                priceValue[travellerSignatureOrXType].optimisation = totalOrBaseOrTaxSavingTraveller?.currencyArray?.length ? totalOrBaseOrTaxSavingTraveller?.currencyArray[0] : null;
                            }
                        } else {
                            priceValue[travellerSignatureOrXType] = {
                                rate: totalOrBaseOrTaxTraveller?.currencyArray?.length ? totalOrBaseOrTaxTraveller?.currencyArray[0] : null,
                                optimisation: totalOrBaseOrTaxSavingTraveller?.currencyArray?.length ? totalOrBaseOrTaxSavingTraveller?.currencyArray[0] : null
                            };
                        }
                    } else {
                        const totalOrBaseOrTaxTraveller: PriceItemType = totalOrBaseOrTax?.totalPerTravellerArray?.find((totalPerTraveller: TotalPerTravellerType): boolean => totalPerTraveller.traveller?.signature === traveller?.signature)?.total;
                        priceValue[travellerSignatureOrXType] = {
                            rate: totalOrBaseOrTaxTraveller?.currencyArray?.length ? totalOrBaseOrTaxTraveller?.currencyArray[0] : null,
                            optimisation: null
                        };
                    }
                });
                values.push(priceValue);
            }
        });
        return values;
    }

    public getFirstTravellerByType(travellers: TravellerType[]): TravellerType[] {
        const firstTravellerByType: TravellerType [] = [];
        Object.keys(TravellerPrimitiveXType).forEach((key: string): void => {
            firstTravellerByType.push(travellers.find((traveller: TravellerType): boolean => traveller?.xType.toLowerCase() === key));
        });
        return firstTravellerByType;
    }

    public constructRulesDetailData(price: PriceInBookingType, travellers: TravellerType[], costOrRetail: string, booking: BookingType): IRuleTable[] {
        if (booking) {
            const values: IRuleTable[] = [];
            const firstTravellerByType: TravellerType[] = this.getFirstTravellerByType(travellers);
            const rate: RateInBookingType = price[costOrRetail]?.detailArray?.length ? price[costOrRetail]?.detailArray[0]?.rate : '-';
            const detailArray: PriceDetailInBookingType = price[costOrRetail]?.detailArray?.length ? price[costOrRetail]?.detailArray[0] : '-';
            detailArray?.sectorArray?.forEach((sector: SectorXRefType): void => {
                if (booking.sectorArray.find((sectorFromBooking: SectorInBookingType): boolean => sectorFromBooking.signatureArray[0] === sector.signature)) {
                    const priceValue: IRuleTable = {
                        sectorLine: sector.signature || sector.xRef,
                    };
                    firstTravellerByType.forEach((traveller: TravellerType): void => {
                        if (traveller) {
                            (rate?.ruleArray ?? []).forEach((rule: RuleType): void => {
                                rule.sectorArray.forEach((sectorRule: SectorXRefType): void => {
                                    if (sectorRule.signature === sector.signature || sectorRule.xRef === sector.xRef) {
                                        priceValue[traveller.xType] = {rule};
                                    }
                                });
                            });
                            rate?.fareComponentArray?.forEach((fareComponent: FareComponentInBookingType): void => {
                                fareComponent.sectorArray?.forEach((sectorRule: SectorXRefType): void => {
                                    if (sectorRule.xRef === sector.xRef || sectorRule.signature === sector.signature) {
                                        priceValue[traveller.xType] = {...priceValue[traveller.xType], fareComponent};
                                    }
                                });
                            });
                        }
                    });
                    values.push(priceValue);
                }
            });
            return values;
        }
    }

    public getTotal(bookingTotal: PriceItemType[], travellers: TravellerType[]): IPricesTable {
        const totalTransportation: PriceItemType = bookingTotal.find((totalItem: PriceItemType): boolean => !Boolean(totalItem.xReason));

        const totalValue: CurrencyUnitTotalType | string = totalTransportation?.currencyArray.length ? totalTransportation?.currencyArray[0] : '-';

        const priceValue: IPricesTable = {
            [EColumnType.rateTitle]: Yii.t('wui-sasha-js', 'Total'),
            totalValue,
        };

        travellers.forEach((traveller: TravellerType): void => {
            const totalTraveller: PriceItemType = totalTransportation.totalPerTravellerArray?.find((totalPerTraveller: TotalPerTravellerType): boolean => totalPerTraveller.traveller?.signature === traveller?.signature)?.total;
            priceValue[traveller.signature] = totalTraveller?.currencyArray?.length ? totalTraveller?.currencyArray[0] : '-';
        });

        return priceValue;
    }

    public formatSectorRoutingDifferences(differences: DifferenceType[], sectionSeatAndDates: ISectorSeatAndDates, sector: SectorInBookingType): ISectorDifferences {
        const allDifferences: ISectorDifferences = {
            beginDateDifference: {
                otherValue: sectionSeatAndDates.beginDate,
                actualValue: sectionSeatAndDates.beginDate,
                impact: null
            },
            endDateDifference: {
                otherValue: sectionSeatAndDates.endDate,
                actualValue: sectionSeatAndDates.endDate,
                impact: null
            },
            seatCodeDifference: {
                otherValue: sectionSeatAndDates.seatCode,
                actualValue: sectionSeatAndDates.seatCode,
                impact: null
            }
        };
        const sectionDifferenceArray: SectorInDifferenceType[] = (differences || [])
            .map((difference: DifferenceType): SectorInDifferenceType =>
                difference.sectorArray?.find((sectorItem: SectorInDifferenceType): boolean =>
                    sectorItem.signature === sector.signatureArray?.[0] &&
                    sectorItem.scope === "Offer" &&
                    !!(sectorItem.seat || sectorItem.end || sectorItem.begin)))
            .filter((element: SectorInDifferenceType): boolean => element !== undefined);
        // get value from scope offer and booking
        for (const item of sectionDifferenceArray) {
            if (item.seat?.code) {
                allDifferences.seatCodeDifference.actualValue = item.seat?.code;
                allDifferences.seatCodeDifference.impact = item.seat?.grade;
            }
        }
        return allDifferences;
    }

    public getSingleServiceFromBooking(services: ServiceInBookingType[], price: PriceInBookingType, costOrRetail: string): ServiceInBookingType {
        let serviceXRef: string;
        if (price[costOrRetail]?.detailArray?.length && price[costOrRetail]?.detailArray[0].serviceArray?.length) {
            serviceXRef = price[costOrRetail].detailArray[0].serviceArray[0].xRef;
        }

        return (services ?? []).find((service: ServiceInBookingType): boolean => service.xId === serviceXRef);
    }

    public constructSectorDetail(price: PriceInBookingType, costOrRetail: string, sector: SectorInBookingType, differences?: DifferenceType[], typoPax: string = "Adult"): ISectorDetail {

        const unknownString: string = Yii.t('wui-sasha-js', 'Inconnu');
        let cancellationBeforeDeparture: SafeHtml = unknownString;
        let cancellationAfterDeparture: SafeHtml = unknownString;
        let exchangeBeforeDeparture: SafeHtml = unknownString;
        let exchangeAfterDeparture: SafeHtml = unknownString;

        const rate: RateInBookingType = this.findRateFromPaxType(price[costOrRetail].detailArray, typoPax);

        if (!rate) {
            return;
        }

        const fareComponent: FareComponentInBookingType = rate?.fareComponentArray?.find((fareComponentItem: FareComponentInBookingType): SectorXRefWithSeatCodeType =>
            fareComponentItem.sectorArray?.find((sectorRule: SectorXRefType): boolean => sectorRule.signature === sector.signatureArray?.[0] || sectorRule.xRef === sector.xId)
        );

        const luggageRule: RuleType = this.getRule(rate, sector, "Luggage");
        const exchangeRule: RuleType = this.getRule(rate, sector, "Exchange");
        const cancellationRule: RuleType = this.getRule(rate, sector, "Cancellation");


        if (exchangeRule?.penaltyArray?.length) {
            exchangeBeforeDeparture = this.getPenaltyCurrency(exchangeRule.penaltyArray, EPenaltyType.BeforeDeparture);
            exchangeAfterDeparture = this.getPenaltyCurrency(exchangeRule.penaltyArray, EPenaltyType.AfterDeparture);
        }

        if (cancellationRule?.penaltyArray?.length) {
            cancellationBeforeDeparture = this.getPenaltyCurrency(cancellationRule.penaltyArray, EPenaltyType.BeforeDeparture);
            cancellationAfterDeparture = this.getPenaltyCurrency(cancellationRule.penaltyArray, EPenaltyType.AfterDeparture);
        }

        const sectorDetailFormatted: ISectorDetail = {
            sectorSignature: sector.signatureArray?.[0],
            ff: fareComponent?.fareFamily?.name?.value,
            fb: fareComponent?.fareBasis?.value,
            luggageUnit: this.luggageUnit.transform(luggageRule?.luggage?.weight?.xUnit),
            luggageWeight: luggageRule?.luggage?.weight?.max?.toString(),
            luggageQuantity: luggageRule?.luggage?.quantity?.max || luggageRule?.luggage?.quantity?.max === 0 ? `${luggageRule.luggage.quantity.max} PC` : '',
            exchangeable: this.isExchangeable(rate) ? Yii.t('wui-sasha-js', 'Ech.') : Yii.t('wui-sasha-js', 'Non Ech.'),
            cancellable: this.isCancellable(rate) ? Yii.t('wui-sasha-js', 'Ann.') : Yii.t('wui-sasha-js', 'Non Ann.'),
            cancellationBeforeDeparture,
            cancellationAfterDeparture,
            exchangeBeforeDeparture,
            exchangeAfterDeparture
        };
        return this.formatSectorDetailDifferences(differences, sectorDetailFormatted, this.getSignaturesByTypoPax(price[costOrRetail].detailArray, typoPax));
    }

    public checkCancelAndExchangeDifferences(detailArray: PriceDetailInBookingType[]): ICancelAndExchange {
        const rateFound: RateInBookingType = this.findRateFromPaxType(detailArray);
        if (rateFound) {
            return {
                exchangeable: this.isExchangeable(rateFound),
                cancellable: this.isCancellable(rateFound)
            };
        }
    }

    private getTotalOrBaseOrTaxType(key: string): string {
        switch (key) {
            case ETarificationLineType.base :
                return ETarificationArrayType.baseArray;
            case ETarificationLineType.tax :
                return ETarificationArrayType.taxArray;
            case ETarificationLineType.surcharge :
                return ETarificationArrayType.surchargeArray;
            default :
                return ETarificationArrayType.totalArray;
        }
    }

    private getPriceByXReason(total: PriceTotalsContentInBookingType, totalOrBaseOrTaxObject: string, xRole?: string): PriceItemType {
        return total?.[totalOrBaseOrTaxObject]?.find(
            (totalItem: PriceItemType): boolean => {
                return totalOrBaseOrTaxObject === ETarificationArrayType.totalArray ?
                    !totalItem.xReason :
                    totalItem.xReason === xRole;
            });
    }

    private getPriceByTraveller(totalOrBaseOrTax: PriceItemType, signature: string): PriceItemType {
        return totalOrBaseOrTax?.totalPerTravellerArray?.find(
            (totalPerTraveller: TotalPerTravellerType): boolean => {
                return totalPerTraveller.traveller?.signature === signature;
            })?.total;
    }

    private getPenaltyCurrency(penalties: PenaltyType[], xCountingFrom: string): SafeHtml {
        const penaltyItem: PenaltyType = penalties.find((penalty: PenaltyType): boolean => penalty.xCountingFrom === xCountingFrom);
        if (penaltyItem) {
            const output: SafeHtml = penaltyItem?.currency?.value ?
                this.priceRealAmount.transform(penaltyItem?.currency?.value, penaltyItem?.currency?.currencyCode) :
                `${penaltyItem?.percentage}&nbsp;%`;
            return output || Yii.t('wui-sasha-js', 'Inconnu');
        }
        return Yii.t('wui-sasha-js', 'Inconnu');
    }

    private getRule(rate: RateInBookingType, sector: SectorInBookingType, xRole: XRoleForRuleType): RuleType {
        return (rate?.ruleArray ?? []).find((ruleItem: RuleType): SectorXRefType => {
            return ruleItem.xRole === xRole &&
                ruleItem.sectorArray.find(
                    (sectorRule: SectorXRefType): boolean => {
                        return sectorRule.signature === sector.signatureArray?.[0] ||
                            sectorRule.xRef === sector.xId;
                    })
                ;
        });
    }

    private getSignaturesByTypoPax(detailArray: PriceDetailInBookingType[], typoPax: string): string[] {
        const typoPaxSignatures: string[] = [];
        detailArray?.find(
            (detail: PriceDetailInBookingType): RateInBookingType => (
                detail.travellerArray?.find((traveller: TravellerType): void => {
                    if (traveller?.xType === typoPax && !typoPaxSignatures.includes(traveller.signature)) {
                        typoPaxSignatures.push(traveller.signature);
                    }
                }))
        );
        return typoPaxSignatures;
    }

    private formatSectorDetailDifferences(differences: DifferenceType[], sectorDetail: ISectorDetail, typoPaxSignatures: string[] = []): ISectorDetailWithDifferences {
        const result: ISectorAdultDifferences = {
            ...sectorDetail,
            fareFamilyDifference: {
                actualValue: sectorDetail.ff,
                impact: null
            },
            fareBasisDifference: {
                actualValue: sectorDetail.fb,
                impact: null
            },
            luggageQuantityDifference: {
                actualValue: sectorDetail.luggageQuantity,
                impact: null
            },
            luggageUnitDifference: {
                actualValue: sectorDetail.luggageUnit,
                impact: null
            },
            luggageWeightDifference: {
                actualValue: sectorDetail.luggageWeight,
                impact: null
            },
            cancellableDifference: {
                actualValue: sectorDetail.cancellable,
                impact: null
            },
            exchangeableDifference: {
                actualValue: sectorDetail.exchangeable,
                impact: null
            },
            cancellationBeforeDepartureDifference: {
                actualValue: sectorDetail.cancellationBeforeDeparture,
                impact: null
            },
            cancellationAfterDepartureDifference: {
                actualValue: sectorDetail.cancellationAfterDeparture,
                impact: null
            },
            exchangeBeforeDepartureDifference: {
                actualValue: sectorDetail.exchangeBeforeDeparture,
                impact: null
            },
            exchangeAfterDepartureDifference: {
                actualValue: sectorDetail.exchangeAfterDeparture,
                impact: null
            },
        };

        const sectionDifferenceArray: DifferenceType[] = (differences || [])
            .filter((difference: DifferenceType): boolean => {
                    const hasSectorWithSameSignature: boolean = !!difference.sectorArray?.find(
                        (sectorItem: SectorInDifferenceType): boolean => {
                            return sectorItem.signature === sectorDetail.sectorSignature;
                        });
                    const hasRates: boolean = !!difference.rateArray?.length;
                    const hasTravellerWithSameSignature: boolean = difference?.traveller && typoPaxSignatures.includes(difference.traveller.signature);
                    return hasSectorWithSameSignature && hasRates && hasTravellerWithSameSignature;
                }
            );

        sectionDifferenceArray.forEach((item: DifferenceType, index: number): void => {
            const bookingValue: RateInDifferenceType = item.rateArray?.find((rate: RateInDifferenceType): boolean => rate.scope === "Booking");
            const offerValue: RateInDifferenceType = item.rateArray?.find((rate: RateInDifferenceType): boolean => rate.scope === "Offer");

            if (bookingValue.fareBasisArray?.length) {
                result.fareBasisDifference.impact = offerValue.fareBasisArray?.[0]?.grade;
            }

            if (bookingValue.fareFamilyArray?.length) {
                result.fareFamilyDifference.impact = offerValue.fareFamilyArray?.[0]?.grade;
            }

            if (bookingValue.luggageArray?.length) {
                result.luggageQuantityDifference.impact = offerValue.luggageArray
                    .find((luggage: LuggageInDifferenceType): boolean => {
                        return luggage.quantity?.hasOwnProperty("max");
                    })?.grade;
                result.luggageUnitDifference.impact = offerValue.luggageArray
                    .find((luggage: LuggageInDifferenceType): boolean => {
                        return !!luggage.quantity?.hasOwnProperty("xUnit");
                    })?.grade;
                result.luggageWeightDifference.impact = offerValue.luggageArray
                    .find((luggage: LuggageInDifferenceType): boolean => {
                        return luggage.hasOwnProperty("weight");
                    })?.grade;
            }

            if (offerValue.penaltyArray?.length) {
                offerValue.penaltyArray.filter((penaltyItem: PenaltyType): void => {
                    if (penaltyItem.xRole === "Cancel") {
                        if (penaltyItem.xCountingFrom === EPenaltyType.BeforeDeparture) {
                            const impact: string = offerValue.penaltyArray
                                .find((penalty: PenaltyInDifferenceType): boolean => {
                                    return penalty.xRole === "Cancel" && penalty.xCountingFrom === EPenaltyType.BeforeDeparture;
                                })?.grade;
                            result.cancellationBeforeDepartureDifference.impact = impact;
                        } else if (penaltyItem.xCountingFrom === EPenaltyType.AfterDeparture) {
                            const impact: string = offerValue.penaltyArray
                                .find((penalty: PenaltyInDifferenceType): boolean => {
                                    return penalty.xRole === "Cancel" &&
                                        penalty.xCountingFrom === EPenaltyType.AfterDeparture;
                                })?.grade;
                            result.cancellationAfterDepartureDifference.impact = result.cancellationBeforeDepartureDifference.impact = impact;
                        }
                    }
                    if (penaltyItem.xRole === "Exchange") {
                        if (penaltyItem.xCountingFrom === EPenaltyType.BeforeDeparture) {
                            const impact: string = offerValue.penaltyArray
                                .find((penalty: PenaltyInDifferenceType): boolean => {
                                    return penalty.xRole === "Exchange" &&
                                        penalty.xCountingFrom === EPenaltyType.BeforeDeparture;
                                })?.grade;
                            result.exchangeBeforeDepartureDifference.impact = result.cancellationBeforeDepartureDifference.impact = impact;
                        } else if (penaltyItem.xCountingFrom === EPenaltyType.AfterDeparture) {
                            result.exchangeAfterDepartureDifference.impact = offerValue.penaltyArray
                                .find((penalty: PenaltyInDifferenceType): boolean => {
                                    return penalty.xRole === "Exchange" &&
                                        penalty.xCountingFrom === EPenaltyType.AfterDeparture;
                                })?.grade;
                        }
                    }
                });
            }
        });
        return result;
    }

    private findRateFromPaxType(detailArray: PriceDetailInBookingType[], typoPax: string = "Adult"): RateInBookingType | undefined {
        return detailArray?.find(
            (detail: PriceDetailInBookingType): RateInBookingType => {
                return (detail.travellerArray.find((traveller: TravellerType): boolean => traveller?.xType === typoPax))
                    && detail.rate;
            }
        )?.rate;
    }

    private isExchangeable(rate?: RateInBookingType): boolean {
        return rate?.keywordArray?.find((keyword: KeywordType): boolean => keyword.xCode && keyword.xCode === 'Exchangeable') !== undefined;
    }

    private isCancellable(rate?: RateInBookingType): boolean {
        return rate?.keywordArray?.find((keyword: KeywordType): boolean => keyword.xCode && keyword.xCode === 'Cancellable') !== undefined;
    }

    private getServiceData(services: ServiceInBookingType[], price: PriceInBookingType, costOrRetail: string): string {
        let serviceXRef: string;
        if (price[costOrRetail]?.detailArray?.length && price[costOrRetail]?.detailArray[0].serviceArray?.length) {
            serviceXRef = price[costOrRetail].detailArray[0].serviceArray[0].xRef;
        }

        if (!serviceXRef && !services?.length) {
            return "";
        }
        let serviceData: string = `${price[costOrRetail]?.index ? ` ${price[costOrRetail]?.index}` : ''}`;
        const linkedService: ServiceInBookingType = (services ?? []).find((service: ServiceInBookingType): boolean => service.xId === serviceXRef);

        if (!linkedService) {
            return serviceData;
        }
        serviceData += " - ";
        if (linkedService.subCategory?.code) {
            serviceData += linkedService.subCategory?.code;
        } else {
            serviceData += linkedService.xCategory;
        }

        const description: string = this.getServiceDescription(linkedService);
        if (description) {
            serviceData += ` - ${this.labelService.completeLabel(Yii.t('wui-sasha-js', '%description%'), {
                description: this.getServiceDescription(linkedService)
            })}`;
        }

        return serviceData;
    }

    private getServiceDescription(linkedService: ServiceInBookingType): string {
        if (linkedService.description?.title) {
            return linkedService.description?.title;
        }

        if (linkedService.description?.textLineArray?.length) {
            return linkedService.description?.textLineArray[0];
        }

        if (linkedService.nameArray?.length) {
            return linkedService.nameArray[0].value;
        }

        return "";
    }

    private getFeeData(price: PriceInBookingType, costOrRetail: string): string {
        let feeData: string = price[costOrRetail]?.index ? ` ${price[costOrRetail]?.index.toString()}` : '';
        const paymentType: XReasonType = "Payment";
        if (price[costOrRetail].total?.feeArray?.length) {
            const feeElement: PriceItemType = price[costOrRetail].total?.feeArray.find(
                (fee: PriceItemType): boolean => fee.xReason.toLowerCase() === paymentType.toLowerCase());
            feeData += ` - ${feeElement.xApply}`;
        }
        if (price[costOrRetail].xState) {
            feeData += ` - ${price[costOrRetail].xState}`;
        }
        return feeData;
    }
}
